import {ActionTree} from "vuex";
import {RootState} from "../types/RootState";
import {ServicesRestoreData, ServicesState} from "../types/ServicesState";
import { servicesKey, commonCssKey } from "@service/helpers/sessionHelper";
import {repositories} from "../../api/Repositories";
import {RepositoryFabric} from "@api/repositories/RepositoryFabric";
import {DateTime} from "luxon";
import {FreshChat, prepareUserData} from "@common/FreshChat";
import { ArrayHelpers } from '@common/ArrayHelpers';
import { addStylesToHead } from "@service/helpers/domHelper";
import { STYLES_FROM_CONFIG_NAME } from "@common/constants";
import {ConfigurationRepository} from "@api/repositories/ConfigurationRepository";

export const actions: ActionTree<ServicesState, RootState> = {
    restore({ dispatch, commit }, data) {
        try {
            const storedValue = JSON.parse(localStorage.getItem(servicesKey(data.backend)) as string);

            if (storedValue) {
                const storedCommonCss = localStorage.getItem(commonCssKey(data.backend)) as string | null;

                // Fallback for old saved values
                if (!Object.prototype.hasOwnProperty.call(storedValue, 'services')) {
                    commit('restore', {
                        services: storedValue,
                        commonCss: storedCommonCss,
                        updateTime: null
                    } as ServicesRestoreData);
                } else {
                    commit('restore', {
                        services: storedValue.services,
                        commonCss: storedCommonCss,
                        updateTime: storedValue.updateTime ? DateTime.fromISO(storedValue.updateTime) : null
                    } as ServicesRestoreData);
                }
            }
        } catch (e) {
            dispatch('reportError', e, {root: true});
        }
    },

    async fetch({commit, getters, state}, backend: string) {
        const configurationRepository = repositories.get<ConfigurationRepository>(RepositoryFabric.CONFIGURATION);
        const configuration = await configurationRepository.appropriateConfiguration('Web');

        if (configuration?.commonCss) {
            addStylesToHead(configuration.commonCss, STYLES_FROM_CONFIG_NAME);
        }

        commit('setCommonCss', {
            css: configuration?.commonCss,
            backend: backend
        });

        if (configuration && configuration.servicesParams) {
            commit('setUpdateTime', DateTime.local());
            const currentServiceNames = Object.keys(state.services);

            let newServiceNames: string[] = [];
            configuration.servicesParams.forEach((service: any) => {
                if (getters['isValidServiceName'](service.name)) {
                    commit('setServiceParams', {
                        service: service,
                        backend: backend
                    });
                    newServiceNames.push(service.name);
                }
            });

            const removedServiceNames = ArrayHelpers.difference(currentServiceNames, newServiceNames);
            removedServiceNames.forEach(name => commit('removeServiceParams', name));
        }
    },

    checkUpdates({dispatch, state}, backend: string) {
        if (
            state.updateTime === null ||
            DateTime.local().diff(state.updateTime).milliseconds > 10 * 60 * 1000 ||
            localStorage.getItem(commonCssKey(backend)) === null
        ) {
            dispatch('fetch', backend);
        }
    },

    initFreshChat({commit, getters, rootState}, withUserData) {
        const type = withUserData ? 'auth' : 'unauth';
        const token = getters['freshChatToken'](type);
        const tags = getters['freshChatTags'](type);
        if (token) {
            let userData = null;
            if (withUserData) {
                userData = prepareUserData(rootState);
            }
            const instance = new FreshChat(token, userData, tags);
            commit('setFreshChat', instance)
        }
    },

    setFreshChatUserData({state, rootState}) {
        if (state && state.freshChat) {
            state.freshChat.setUserData(prepareUserData(rootState));
        }
    },

    destroyFreshChat({state, commit}) {
        if (state.freshChat) {
            state.freshChat.destroy();
            commit('setFreshChat', null);
        }
    },

    freshChatFireAfterLogin({state, getters}, type) {
        if (state.freshChat && getters['freshChatFireAfterLogin'](type)) {
            state.freshChat.open();
        }
    }
};

